import ObjectAssignDeep from 'object-assign-deep'
import { decorators, CONSTANTS } from '@sohohouse/platform-resources'
import hash from 'object-hash'
import { platformAction } from '~/helpers/store'
import { SEARCHES, POLL_MAX_TIME, POLL_INTERVAL } from '../constants'
import filterActions from './filters'
import { EVENT_VIEWS } from '~/constants'

const { DateTime } = decorators.shared
const { MINUTE, DAY } = CONSTANTS.DATE_TIME_TYPES
const { MEMBER_EVENT, SCREENING, HOUSE_RIDE, GYM_CLASS } = CONSTANTS.EVENT_TYPES
const { OPEN_FOR_BOOKING } = CONSTANTS.EVENT_STATUSES

export default {
  ...filterActions,

  async pollEvent ({ dispatch, getters }, eventId) {
    const START_TIME = new Date().getTime()
    let thisTime
    let event

    // TODO This is slowing down tests
    // * also this should probably be moved into the specific component where polling is required
    const checkEvent = async () => {
      await new Promise(resolve => setTimeout(resolve, POLL_INTERVAL))
      await dispatch('fetchEvent', { eventId, poll: false })
      event = getters.eventById(eventId)

      thisTime = new Date().getTime()

      if (
        thisTime < (START_TIME + POLL_MAX_TIME) &&
        event.booking && event.booking.unprocessed
      ) {
        return checkEvent()
      }

      return event
    }

    return checkEvent()
  },
  async fetchEvent ({ commit, getters, dispatch }, opts) {
    let poll
    let eventId
    if (typeof opts === 'string') {
      eventId = opts
      poll = true
    } else {
      eventId = opts.eventId
      poll = opts.poll
    }

    await platformAction({
      client: this.$api.events,
      method: 'get',
      params: eventId,
      commit,
      logError: this.$logError,
      include: ['booking', 'resource', 'film', 'translations', 'venue']
    })

    const event = getters.eventById(eventId)
    if (event && event.booking && event.booking.unprocessed && poll) {
      dispatch('pollEvent', eventId)
    }
  },

  async fetchEvents ({ commit, getters }, { eventView, params = {}, page = 1 }) {
    const withEventViewFilters = getters.applyEventViewFilters({ eventView, params })
    const hashedSearch = hash(withEventViewFilters)

    const commitWrapper = (mutation, mutationParams) => {
      switch (mutation) {
        case 'fetching':
          commit('searchFetching', withEventViewFilters)
          break
        case 'fetched':
          commit('searchFetched', withEventViewFilters)
          break
        case 'failed':
          commit('searchFailed', { params: withEventViewFilters, failed: mutationParams })
          break

        default:
          commit(mutation, mutationParams)
      }
    }

    const withPageParams = {
      ...withEventViewFilters,
      filter: {
        ...withEventViewFilters.filter,
        venueDate: {
          from: new DateTime(0).isoString,
          ...withEventViewFilters.filter.venueDate
        },
        endsAt: {
          from: new DateTime().setStartOf(MINUTE).isoString
        }
      },
      page
    }

    try {
      const { data: events, meta } = await platformAction({
        client: this.$api.events,
        params: withPageParams,
        commit: commitWrapper,
        logError: this.$logError,
        simple: false,
        include: ['resource', 'film', 'translations', 'booking', 'venue']
      })

      commit('saveSearch', { hashedSearch, params: withEventViewFilters, events, meta })

      return { hashedSearch, hasMorePages: meta && meta.totalPages > page }
    } catch (e) {
      console.error('Failed to get events', e)
      return { error: true }
    }
  },

  async fetchHappeningNow ({ commit, rootGetters }) {
    const favoriteVenues = rootGetters['user/account/favoriteVenues']
    const localHouse = rootGetters['user/account/localHouse']

    const parentLocationId = favoriteVenues.map(v => v.id)

    parentLocationId.push(localHouse.id)

    const startOfDay = new DateTime().setStartOf(DAY).isoShortString
    const endDate = new DateTime().setStartOf(DAY).add(7, DAY).isoShortString

    const commitWrapper = mutation => {
      switch (mutation) {
        case 'fetching':
          commit('happeningNowFetching')
          break
        case 'fetched':
          commit('happeningNowFetched')
          break
        case 'failed':
          commit('happeningNowFailed')
          break
        default:
          commit(mutation)
          break
      }
    }

    const events = await platformAction({
      client: this.$api.events,
      method: 'getAll',
      params: {
        perPage: 10,
        filter: {
          parentLocationId: parentLocationId.join(','),
          venueDate: {
            from: startOfDay,
            to: endDate
          },
          venueEndDate: {
            from: startOfDay,
            to: endDate
          },
          eventType: [MEMBER_EVENT, GYM_CLASS, HOUSE_RIDE, SCREENING].join(','),
          state: OPEN_FOR_BOOKING
        }
      },
      include: ['venue', 'booking'],
      commit: commitWrapper,
      commitData: false,
      logError: this.$logError
    })

    commit('saveHappeningNow', events)
  },

  fetchInductionEvents: async ({ dispatch, commit }, filter) => {
    const params = ObjectAssignDeep({}, SEARCHES.INDUCTION, { filter })
    commit('inductionEventsFetching')
    await dispatch('fetchEvents', { params })
    commit('inductionEventsFetched')
  },

  async fetchPastEvents ({ commit }, { eventView, page = 1, perPage = 6 }) {
    const { eventTypes } = EVENT_VIEWS[eventView]
    const events = await platformAction({
      client: this.$api.events,
      method: 'getAll',
      params: {
        perPage,
        filter: {
          eventType: eventTypes.join(','),
          startsAt: {
            from: new DateTime().subtract(99, 'years').isoString,
            to: new DateTime().setStartOf(MINUTE).isoString
          },
        },
        sort: '-starts_at',
        page
      },
      include: ['venue', 'booking'],
      commit,
      commitData: false,
      logError: this.$logError
    }) || []

    const hasMorePages = events.length >= perPage

    if (page === 1) {
      commit('setPastEvents', events)
    } else {
      commit('appendPastEvents', events)
    }

    return { page, hasMorePages, events }
  },
}
